import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faPenToSquare,
  faPlus,
  faTrashCan,
  faUser,
  faRotateLeft,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import Drawer from "@/components/ui/drawer";
import FormInput from "@/components/ui/form-input";
import TextArea from "@/components/ui/form-input/TextArea";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import { CC_ERROR_TEXT, CC_FIELD, CC_PANEL, CC_PANEL_FLUSH } from "@/components/case-checking/caseCheckingChrome";
import { ICON_CHECK_PASSED } from "@/components/case-checking/caseCheckingStatusBadges";
import { RemedialActionsAgentBanner } from "@/components/case-checking/RemedialActionsAgentBanner";
import {
  REMEDIAL_PRIORITY_META,
  REMEDIAL_PRIORITY_SELECT_OPTIONS,
  sortRemedialActionsByPriority,
} from "@/components/case-checking/remedialActionPriority";
import { CoverageGapBanner } from "@/components/case-checking/coverageGapBanner";
import { findRuleCoverageGaps, ruleIdsFor } from "@/components/case-checking/remedialCoverage";
import { useHumanRuleReviews } from "@/components/case-checking/useHumanRuleReviews";
import {
  mergeRemedialActions,
  type ActionEditTag,
  type EffectiveRemedialAction,
  isOpenEffectiveAction,
} from "@/components/case-checking/remedialActionEdits";
import {
  CASE_ROLES,
  CASE_ROLE_LABELS,
  patchRemedialActionHumanEdits,
  type Assessment,
  type CaseInconsistency,
  type CaseRole,
  type PsRuleHumanReviewRun,
  type RemedialAction,
  type RemedialActionHumanEdit,
  type RemedialActionHumanEditPatch,
  type RemedialActionPriority,
} from "@/services/api/case-checking";

const TAG_CLASS: Record<ActionEditTag, string> = {
  new: "text-indigo-800 ring-indigo-300 bg-indigo-100 dark:text-indigo-300 dark:ring-indigo-500/30 dark:bg-indigo-500/10",
  edited: "text-amber-800 ring-amber-300 bg-amber-100 dark:text-amber-200 dark:ring-amber-500/25 dark:bg-amber-500/10",
  reassigned: "text-sky-800 ring-sky-300 bg-sky-100 dark:text-sky-200 dark:ring-sky-500/25 dark:bg-sky-500/10",
};

function ActionPriorityBadge({ priority }: { priority: RemedialActionPriority }) {
  const meta = REMEDIAL_PRIORITY_META[priority];
  return (
    <span
      className={clsx(
        "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset",
        meta.badgeClass,
      )}
    >
      {meta.label}
    </span>
  );
}

function ActionTag({ tag }: { tag: ActionEditTag }) {
  const label = tag === "new" ? "New" : tag === "edited" ? "Edited" : "Reassigned";
  return (
    <span
      className={clsx(
        "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset",
        TAG_CLASS[tag],
      )}
    >
      {label}
    </span>
  );
}

function ActionEditDrawer({
  action,
  mode,
  onClose,
  onSave,
  saving,
}: {
  action: Partial<EffectiveRemedialAction> & { id: string };
  mode: "edit" | "create" | "reassign";
  onClose: () => void;
  onSave: (patch: RemedialActionHumanEditPatch) => void;
  saving: boolean;
}) {
  const [title, setTitle] = useState(action.title ?? "");
  const [description, setDescription] = useState(action.description ?? "");
  const [role, setRole] = useState<CaseRole>((action.role as CaseRole) ?? "planner");
  const [priority, setPriority] = useState<RemedialActionPriority>(action.priority ?? "medium");

  const prioritySelectOptions: SelectOption<RemedialActionPriority>[] = useMemo(
    () => REMEDIAL_PRIORITY_SELECT_OPTIONS.map((o) => ({ value: o.id, label: o.label })),
    [],
  );
  const priorityHelper = REMEDIAL_PRIORITY_META[priority].helper;
  const roleSelectOptions: SelectOption<CaseRole>[] = useMemo(
    () => CASE_ROLES.map((r) => ({ value: r, label: CASE_ROLE_LABELS[r] })),
    [],
  );

  const save = () => {
    onSave({
      action_id: action.id,
      created: mode === "create",
      deleted: false,
      title: title.trim(),
      description: description.trim(),
      role,
      priority,
      rule_ids: action.rule_ids ?? [],
      edited_at: null,
    });
  };

  const titleLabel = mode === "reassign" ? "Reassign action" : mode === "create" ? "Add action" : "Edit action";

  return (
    <Drawer isOpen onClose={onClose} title={titleLabel} width="w-full max-w-xl">
      <div className="w-full min-w-0 space-y-4 px-6 pb-8 pt-1">
        {mode !== "reassign" ? (
          <>
            <label className="block">
              <span className="text-xs text-slate-500">Title</span>
              <FormInput
                className={clsx(CC_FIELD, "mt-1 rounded-md")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Description</span>
              <TextArea
                className={clsx(CC_FIELD, "mt-1 min-h-[6rem] resize-none rounded-md")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Priority</span>
              <SelectDropdown
                options={prioritySelectOptions}
                value={priority}
                onChange={setPriority}
                className="mt-1 w-full [&>div]:!mt-0"
                buttonClassName={clsx(CC_FIELD, "!cursor-pointer rounded-md")}
                dropdownWidth="w-full min-w-full"
              />
              <p className="mt-1.5 text-xs text-slate-500">{priorityHelper}</p>
            </label>
          </>
        ) : null}
        <label className="block">
          <span className="text-xs text-slate-500">Assigned to</span>
          <SelectDropdown
            options={roleSelectOptions}
            value={role}
            onChange={setRole}
            className="mt-1 w-full [&>div]:!mt-0"
            buttonClassName={clsx(CC_FIELD, "!cursor-pointer rounded-md")}
            dropdownWidth="w-full min-w-full"
          />
        </label>
        <div className="flex gap-2 pt-2">
          <Button color="indigo" onClick={save} disabled={saving || (!title.trim() && mode !== "reassign")}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button plain onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

function ActionRow({
  action,
  onEdit,
  onReassign,
  onDelete,
  onUndo,
}: {
  action: EffectiveRemedialAction;
  onEdit: () => void;
  onReassign: () => void;
  onDelete: () => void;
  onUndo: () => void;
}) {
  const removed = Boolean(action.removed);
  return (
    <div
      className={clsx(
        "flex items-start gap-3 p-4",
        removed && "bg-rose-50/80 dark:bg-rose-950/25 ring-1 ring-inset ring-rose-200/80 dark:ring-rose-500/20",
      )}
      data-testid={`action-row-${action.id}`}
      data-removed={removed ? "true" : "false"}
    >
      <div
        className={clsx(
          "mt-2 h-1.5 w-1.5 shrink-0 rounded-full",
          REMEDIAL_PRIORITY_META[action.priority].dotClass,
          removed && "opacity-40",
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p
            className={clsx(
              "text-sm font-medium text-slate-700 dark:text-slate-200",
              removed && "text-slate-500 line-through decoration-rose-400/80 dark:text-slate-400",
            )}
          >
            {action.title}
          </p>
          <ActionPriorityBadge priority={action.priority} />
          {action.tags.map((tag) => (
            <ActionTag key={tag} tag={tag} />
          ))}
          {removed ? (
            action.autoRemoved ? (
              <span
                className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-800 ring-1 ring-inset ring-sky-300 bg-sky-100 dark:text-sky-200 dark:ring-sky-500/30 dark:bg-sky-500/10"
                title="Linked rules now pass or are no longer unclear"
                data-testid={`action-auto-removed-${action.id}`}
              >
                Auto-removed: linked rules now pass
              </span>
            ) : (
              <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-800 ring-1 ring-inset ring-rose-300 bg-rose-100 dark:text-rose-200 dark:ring-rose-500/30 dark:bg-rose-500/10">
                Removed
              </span>
            )
          ) : null}
        </div>
        <p
          className={clsx(
            "mt-1 text-sm leading-relaxed text-slate-400",
            removed && "line-through decoration-slate-500/50 opacity-70",
          )}
        >
          {action.description}
        </p>
      </div>
      <div className="flex shrink-0 gap-0.5">
        {removed ? (
          <Button plain size="sm" type="button" onClick={onUndo} data-testid={`action-undo-${action.id}`}>
            Undo
          </Button>
        ) : (
          <>
            <Button
              plain
              size="unstyled"
              type="button"
              className="!rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-slate-700 dark:hover:text-slate-200"
              onClick={onEdit}
              title="Edit"
            >
              <FontAwesomeIcon icon={faPenToSquare} className="h-3.5 w-3.5" />
            </Button>
            <Button
              plain
              size="unstyled"
              type="button"
              className="!rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-slate-700 dark:hover:text-slate-200"
              onClick={onReassign}
              title="Reassign"
            >
              <FontAwesomeIcon icon={faUser} className="h-3.5 w-3.5" />
            </Button>
            <Button
              plain
              size="unstyled"
              type="button"
              className="!rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-rose-300"
              onClick={onDelete}
              title="Delete"
            >
              <FontAwesomeIcon icon={faTrashCan} className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function ReviewActionsStep({
  caseId,
  reviewRunId,
  agentActions,
  remedialEdits,
  assessment,
  inconsistencies,
  onEditsChange,
}: {
  caseId: string;
  reviewRunId: string;
  agentActions: RemedialAction[];
  remedialEdits: RemedialActionHumanEdit[];
  assessment?: Assessment | null;
  inconsistencies?: CaseInconsistency[] | null;
  onEditsChange: (run: PsRuleHumanReviewRun) => void;
}) {
  const { overridesByKey } = useHumanRuleReviews(caseId);
  const ruleOverridesSaved = overridesByKey.size > 0;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{
    mode: "edit" | "create" | "reassign";
    action: Partial<EffectiveRemedialAction> & { id: string };
  } | null>(null);

  const effective = useMemo(
    () => mergeRemedialActions(agentActions, remedialEdits),
    [agentActions, remedialEdits],
  );
  const openActions = effective.filter(isOpenEffectiveAction);
  const listActions = effective.filter((a) => isOpenEffectiveAction(a) || a.removed);
  const coverageGaps = useMemo(
    () => findRuleCoverageGaps(assessment ?? null, openActions, inconsistencies),
    [assessment, openActions, inconsistencies],
  );
  const failedGaps = useMemo(() => coverageGaps.filter((g) => g.status === "failed"), [coverageGaps]);
  const unclearGaps = useMemo(() => coverageGaps.filter((g) => g.status === "unclear"), [coverageGaps]);
  const hasRemedialEdits = remedialEdits.length > 0;

  const persistMany = useCallback(
    async (patches: RemedialActionHumanEditPatch[]) => {
      if (patches.length === 0) return;
      setSaving(true);
      setError(null);
      try {
        const updated = await patchRemedialActionHumanEdits(caseId, reviewRunId, patches);
        onEditsChange(updated);
        setDrawer(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to save action");
      } finally {
        setSaving(false);
      }
    },
    [caseId, reviewRunId, onEditsChange],
  );

  const persist = useCallback(
    async (patch: RemedialActionHumanEditPatch) => {
      await persistMany([patch]);
    },
    [persistMany],
  );

  const deletionPatch = (action: EffectiveRemedialAction): RemedialActionHumanEditPatch => ({
    action_id: action.id,
    deleted: true,
    created: action.tags.includes("new"),
    title: action.title,
    description: action.description,
    role: action.role,
    priority: action.priority,
    rule_ids: ruleIdsFor(action),
    edited_at: null,
  });

  const handleDelete = (action: EffectiveRemedialAction) => {
    void persist(deletionPatch(action));
  };

  const handleUndo = (action: EffectiveRemedialAction) => {
    if (action.autoRemoved) {
      void persist({
        action_id: action.id,
        deleted: false,
        created: false,
        title: action.title,
        description: action.description,
        role: action.role,
        priority: action.priority,
        rule_ids: ruleIdsFor(action),
      });
      return;
    }
    if (action.tags.includes("new")) {
      void persist({
        action_id: action.id,
        deleted: false,
        created: true,
        title: action.title,
        description: action.description,
        role: action.role,
        priority: action.priority,
        rule_ids: ruleIdsFor(action),
      });
      return;
    }
    const hadOtherEdits = action.tags.some((t) => t === "edited" || t === "reassigned");
    if (!hadOtherEdits) {
      void persist({ action_id: action.id, clear: true });
      return;
    }
    void persist({
      action_id: action.id,
      deleted: false,
      created: false,
      title: action.title,
      description: action.description,
      role: action.role,
      priority: action.priority,
      rule_ids: ruleIdsFor(action),
      edited_at: null,
    });
  };

  const handleStartOverActions = () => {
    if (!hasRemedialEdits) return;
    if (
      !window.confirm(
        "Start over on actions? This clears your adds, edits, reassignments, and removals for this review step. Rule grades and other review steps are unchanged.",
      )
    ) {
      return;
    }
    void persistMany(remedialEdits.map((edit) => ({ action_id: edit.action_id, clear: true })));
  };

  const byRole = CASE_ROLES.reduce(
    (acc, role) => {
      acc[role] = sortRemedialActionsByPriority(listActions.filter((a) => a.role === role));
      return acc;
    },
    {} as Record<CaseRole, EffectiveRemedialAction[]>,
  );

  const priorityLegend = (
    <p className="text-xs text-slate-500">
      <span className="font-medium text-rose-700 dark:text-rose-300">Must have</span> first, then{" "}
      <span className="font-medium text-amber-700 dark:text-amber-300">Nice to have</span>, then{" "}
      <span className="font-medium text-slate-600 dark:text-slate-400">Optional</span>. Change priority when you
      edit an action.
    </p>
  );

  const actionToolbar = (
    <div className="flex flex-wrap gap-2">
      {hasRemedialEdits ? (
        <Button
          outline
          disabled={saving}
          icon={<FontAwesomeIcon icon={faRotateLeft} className="h-3.5 w-3.5" />}
          onClick={handleStartOverActions}
          data-testid="review-actions-start-over"
        >
          Start over actions
        </Button>
      ) : null}
      <Button
        outline
        icon={<FontAwesomeIcon icon={faPlus} />}
        onClick={() =>
          setDrawer({
            mode: "create",
            action: { id: `human-${crypto.randomUUID()}`, role: "planner", priority: "medium" },
          })
        }
      >
        Add action
      </Button>
    </div>
  );

  if (listActions.length === 0 && coverageGaps.length === 0) {
    return (
      <div className="space-y-3" data-testid="review-actions-empty">
        <RemedialActionsAgentBanner ruleOverridesSaved={ruleOverridesSaved} />
        {priorityLegend}
      <div className={clsx(CC_PANEL, "py-10 text-center")}>
        <FontAwesomeIcon icon={faCircleCheck} className={clsx("mb-4 h-12 w-12", ICON_CHECK_PASSED)} />
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">No actions required</p>
        <p className="mt-1 text-sm text-slate-500">Add an action if something still needs doing.</p>
        <div className="mt-4 flex justify-center">{actionToolbar}</div>
        {drawer ? (
          <ActionEditDrawer
            action={drawer.action}
            mode={drawer.mode}
            onClose={() => setDrawer(null)}
            onSave={(patch) => void persist(patch)}
            saving={saving}
          />
        ) : null}
      </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="review-actions-step">
      <RemedialActionsAgentBanner ruleOverridesSaved={ruleOverridesSaved} />
      {failedGaps.length > 0 ? (
        <CoverageGapBanner tone="danger" title="Failed rules with no remedial" gaps={failedGaps} />
      ) : null}
      {unclearGaps.length > 0 ? (
        <CoverageGapBanner tone="warning" title="Unclear rules with no remedial" gaps={unclearGaps} />
      ) : null}
      {priorityLegend}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          {openActions.length} open action{openActions.length === 1 ? "" : "s"}
        </p>
        {actionToolbar}
      </div>

      {error ? <p className={clsx("text-xs", CC_ERROR_TEXT)}>{error}</p> : null}

      {CASE_ROLES.map((role) => {
        const roleActions = byRole[role];
        if (roleActions.length === 0) return null;
        return (
          <div key={role} className={CC_PANEL_FLUSH}>
            <div className="border-b border-slate-200 dark:border-white/5 px-4 py-2.5">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{CASE_ROLE_LABELS[role]}</span>
            </div>
            <div className="divide-y divide-slate-200 dark:divide-white/5">
              {roleActions.map((action) => (
                <ActionRow
                  key={action.id}
                  action={action}
                  onEdit={() => setDrawer({ mode: "edit", action })}
                  onReassign={() => setDrawer({ mode: "reassign", action })}
                  onDelete={() => handleDelete(action)}
                  onUndo={() => handleUndo(action)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {drawer ? (
        <ActionEditDrawer
          action={drawer.action}
          mode={drawer.mode}
          onClose={() => setDrawer(null)}
          onSave={(patch) => void persist(patch)}
          saving={saving}
        />
      ) : null}
    </div>
  );
}
